/**
 * program 和 exercise 2个层级都 useSwimPoolLength() 来获取数据
 * 数据是单例模式，打开一个 program 的过程都用一个实例
 * 切换 program 后，使用 init 方法重置
 */

import { ref, watch } from 'vue';
import * as corosSportConfig from '@coros/sport-config';
import { distanceM2yard, distanceYard2Cm } from '../../../helper/distance';
import { Modal } from '@arco-design/web-vue';
import { useI18n } from 'vue-i18n';

const poolSwim = corosSportConfig.sport.poolSwim;
const TARGET_UNIT_TYPE = corosSportConfig.basic.TARGET_UNIT_TYPE;

type Option = {
  value: number;
  label: string;
};
type PoolLengthType = 0 | 1 | 2 | 3 | 4 | 5;
// 泳池长度类型，int
const poolLengthType = ref<PoolLengthType>(1);
const previousPoolLengthType = ref<PoolLengthType>(1);
// 自定义泳池长度，数值显示成 m，数据库是 cm
const customPoolLength = ref(100);
// 泳池长度单位，2 m 或者 4 yd
const poolLengthUnit = ref<2 | 4>(2);

const inited = ref(false);
const readonly = ref(false);

// 生成下拉菜单选项，最大值 10000，最小值 poolLength， 每个选择单位长度 poolLength
const distanceOptions = ref<Option[]>([]);

const i18nRef = {
  t: () => {}
};

/**
/**
 * 重置泳池长度
 * @param {int} poolLengthId 泳池长度类型，int
 * @param {int} storedDistance 泳池长度，数值显示成 m，数据库是 cm
 * @param {int} _poolLengthUnit 泳池长度单位，TargetDisplayUnitEnum(METRE:2 YARD:4)
 * @param {boolean} isImperial 是否是英制单位，旧数据才使用
 */
const init = (poolLengthId: PoolLengthType, storedDistance: number, _poolLengthUnit: 2 | 4, _readonly: boolean, isImperial: boolean) => {
  // console.log(`init`, poolLengthId, storedDistance, _poolLengthUnit, _readonly, isImperial);
  readonly.value = _readonly;
  if (typeof poolLengthId !== 'undefined') {
    poolLengthType.value = poolLengthId;
    if (storedDistance) {
      if (_poolLengthUnit === 4) {
        customPoolLength.value = distanceM2yard(storedDistance / 100, 0);
      } else {
        // cm 转 m
        customPoolLength.value = storedDistance / 100;
      }
    } else {
      customPoolLength.value = 100;
    }
    if (typeof _poolLengthUnit === 'number') {
      poolLengthUnit.value = _poolLengthUnit;
    }
  } else {
    customPoolLength.value = 25;
    if (isImperial) {
      poolLengthType.value = poolSwim.POOL_LENGTH_TYPE['25YARD'];
      poolLengthUnit.value = 4;
    } else {
      poolLengthType.value = poolSwim.POOL_LENGTH_TYPE['25M'];
      poolLengthUnit.value = 2;
    }
  }

  initDistanceOptions();

  // 防止监听 poolLengthType 的 watch 执行
  setTimeout(() => {
    if (!inited.value) inited.value = true;
  }, 100);
};

const clear = () => {
  inited.value = false;
  poolLengthType.value = poolSwim.POOL_LENGTH_TYPE['25M'];
  previousPoolLengthType.value = poolSwim.POOL_LENGTH_TYPE['25M'];
  customPoolLength.value = 100;
  poolLengthUnit.value = 2;
  distanceOptions.value = [];
  readonly.value = false;
};

// 泳池长度变了，距离的单位、选项也变化
watch(poolLengthType, (newVal, oldVal) => {
  if (!inited.value || readonly.value) return;

  previousPoolLengthType.value = oldVal;

  // 3 5 是 yd 单位
  if ([3, 5].includes(newVal)) {
    poolLengthUnit.value = 4;
  } else if (!readonly.value) {
    // readonly 模式下不能设置
    poolLengthUnit.value = 2;
  }

  initDistanceOptions();
});
watch(customPoolLength, () => {
  if (!inited.value || readonly.value) return;
  initDistanceOptions();
});

// 初始化距离选项
function initDistanceOptions() {
  let length = poolSwim.poolLengthValueMap[poolLengthType.value];
  console.log(1000,poolSwim)
  if ([poolSwim.POOL_LENGTH_TYPE['33M'], poolSwim.POOL_LENGTH_TYPE['33YARD']].includes(poolLengthType.value)) {
    distanceOptions.value = genereateDistanceOptionsWithFraction(length);
  } else {
    if (poolLengthType.value === poolSwim.POOL_LENGTH_TYPE['CUSTOM']) {
      length = customPoolLength.value;
    }
    distanceOptions.value = genereateDistanceOptions(length);
  }
}

// 生成带分数的距离选项
function genereateDistanceOptionsWithFraction(newVal: number) {
  const newArr: Option[] = [];
  let fraction = 1;
  let start = newVal;
  let count = 1;
  while (start <= 10000) {
    const labelContent = `${start}${fraction ? ' ' + fraction + '/3' : ''}`;
    newArr.push({
      value: Number((start + fraction / 3).toFixed(2)),
      // 33 1/3, 66 2/3, 100, 133 1/3
      label: `${labelContent} (${count} ${i18nRef.t('H6021')})`,
      // input 显示用 labelForDisplay
      labelForDisplay: `${labelContent}`
    });
    start += newVal;
    fraction += 1;
    if (fraction === 3) {
      start += 1;
      fraction = 0;
    }
    count += 1;
  }
  // console.log(``, newArr.slice(0, 10));
  return newArr;
}

// 生成不带分数的距离选项
function genereateDistanceOptions(newVal: number) {
  const newArr: Option[] = [];
  let start = newVal;
  let count = 1;
  while (start <= 10000) {
    newArr.push({
      value: start,
      label: `${start} (${count} ${i18nRef.t('H6021')})`,
      // input 显示用 labelForDisplay
      labelForDisplay: `${start}`
    });
    start += newVal;
    count += 1;
  }
  return newArr;
}

// 改泳池长度后，类似 33 1/3 转 25 逻辑
function convertDistanceValue(value: number) {
  const isInOptions = distanceOptions.value.some((item) => item.value === value);
  if (isInOptions) {
    return value;
  }
  if (poolLengthType.value === 0) {
    return customPoolLength.value;
  }
  // console.log(`convertDistanceValue`, value, previousPoolLengthType.value, poolLengthType.value);
  value = poolSwim.convertDistanceValue(value, previousPoolLengthType.value, poolLengthType.value);
  return value;
}

// 返回 cm 单位直接存数据库
export function getSavePoolLength() {
  // 33 1/3 50 25 25yd 分别是：
  // 3333 5000 2500 2500/0.9144
  if (poolLengthType.value === 0) {
    // 存的时候 yard 转 cm
    if (poolLengthUnit.value === 4) {
      return distanceYard2Cm(customPoolLength.value);
    }
    return customPoolLength.value * 100;
  } else if (poolLengthType.value === 4) {
    return 3333;
  } else if (poolLengthType.value === 5) {
    return Math.round(3333 * 0.9144);
  } else if (poolLengthType.value === 1) {
    return 2500;
  } else if (poolLengthType.value === 2) {
    return 5000;
  } else if (poolLengthType.value === 3) {
    return Math.round(2500 * 0.9144);
  }
}

export function useSwimPoolLength() {
  const { t } = useI18n();
  i18nRef.t = t;

  // 泳池长度变更提示
  function checkAndAlertPoolLengthChange(oldVal: PoolLengthType, newVal: PoolLengthType) {
    if (!inited.value) {
      return;
    }

    // console.log(`poolLengthType`, oldVal, newVal);
    // 50m 到 25m 切换，不提示，其他都显示
    if ((oldVal === 2 && newVal === 1) || (oldVal !== 0 && newVal === 0)) {
      return;
    }
    const getLength = (val) => {
      if (val === 0) return customPoolLength.value + (poolLengthUnit.value === 2 ? 'm' : 'yard');
      return corosSportConfig.sport.poolSwim.getPoolLengthLabelByValue(val);
    };
    let content;
    const isSameUnit = poolLengthUnit.value === poolSwim.poolLengthUnitMap[newVal];
    // 自定义转预设的，旧长度是新长度的倍数且单位一致
    const isOldLengthIsNewLengthMultiple =
      oldVal === 0 && newVal !== 0 && isSameUnit && customPoolLength.value % poolSwim.poolLengthValueMap[newVal] === 0;

    if (isOldLengthIsNewLengthMultiple) {
      //  已设置动作的目标距离不受影响
      content = t('S4688', { oldValue: getLength(oldVal), newValue: getLength(newVal) });
    } else {
      // 泳池长度由{} 变更至{}，已设置动作的目标距离可能发生变更
      content = t('S4687', { oldValue: getLength(oldVal), newValue: getLength(newVal) });
    }

    Modal.confirm({
      title: t('C1001'),
      content,
      okText: t('C1005'),
      cancelText: t('C1006'),
      titleAlign: 'start',
      hideCancel: true,
      modalClass: 'coros-modal-footer-right'
    });
  }

  // 旧数据提示
  function alertSetDefaultPoolLength(swimPoolIsYard: boolean) {
    // 泳池长度默认为{}，已设置动作的目标距离可能发生变更
    const content = t('S4686', { OvO: 25 + (swimPoolIsYard ? 'yard' : 'm') });
    setTimeout(() => {
      Modal.confirm({
        title: t('C1001'),
        content,
        okText: t('C1005'),
        cancelText: t('C1006'),
        titleAlign: 'start',
        hideCancel: true
      });
    }, 100);
  }

  return {
    inited,
    init,
    clear,
    poolLengthType,
    previousPoolLengthType,
    customPoolLength,
    // getPoolLengthOptions() 已经解析好 i18n
    poolLengthOptions: poolSwim.getPoolLengthOptions(),
    poolLengthUnit,
    poolLengthUnitOptions: poolSwim.poolLengthUnitOptions,
    distanceOptions,
    convertDistanceValue,
    checkAndAlertPoolLengthChange,
    alertSetDefaultPoolLength
  };
}
